import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  SendOutlined,
  CreditCardFilled,
  MailOutlined,
  AppstoreFilled,
} from '@ant-design/icons'
import { v4 } from 'uuid'
import { FacturationGroups } from '../facturation/facturationGroups'
import { CgvDataJson, parseCgvDataJson } from '../facturation/cgvDataJsonForm'
import { CgvEditModal, copyCgvSignatureLink } from '../facturation/cgvEditModal'
import { EMAIL_FACTURATION_REGEX } from '../facturation/facturationGroupsLogic'
import {
  FacturationCgvPayload,
  SoftaccessFactu,
  facturationCgvService,
} from '../facturation/facturationCgvService'

function softLabel(type: 'cnegoce' | 'mspro') {
  return type === 'mspro' ? 'MsPro' : 'Scan & Conquest'
}

const chipStyle: React.CSSProperties = {
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  borderRadius: 8,
  padding: '10px 12px',
  height: '100%',
}

interface Props {
  orgaId: string
  orgaSiret?: string | null
}

export function OrganisationFacturationCgv({ orgaId, orgaSiret }: Props) {
  const [loading, setLoading] = useState(true)
  const [payload, setPayload] = useState<FacturationCgvPayload | null>(null)
  const [selectedSoftId, setSelectedSoftId] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [cgvModalOpen, setCgvModalOpen] = useState(false)
  const [emailFacturation, setEmailFacturation] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await facturationCgvService.getFacturationCgvByOrga(orgaId)
      setPayload(data)
      const first = data.softaccesses[0]
      setSelectedSoftId(prev => {
        if (prev && data.softaccesses.some(s => s.softaccess_id === prev)) return prev
        return first?.softaccess_id
      })
    } catch (e) {
      console.error(e)
      message.error('Impossible de charger CGV / facturation')
    } finally {
      setLoading(false)
    }
  }, [orgaId])

  useEffect(() => {
    load()
  }, [load])

  const softaccessFactuData = useMemo(
    () => payload?.softaccesses.find(s => s.softaccess_id === selectedSoftId),
    [payload, selectedSoftId]
  )

  const groupements = useMemo(
    () =>
      (payload?.cnGroupements || []).filter(
        g => softaccessFactuData && g.softaccessType === softaccessFactuData.softaccess_type
      ),
    [payload, softaccessFactuData]
  )

  useEffect(() => {
    if (!softaccessFactuData) return
    setEmailFacturation(softaccessFactuData.facturation_email || '')
  }, [softaccessFactuData?.softaccess_id, softaccessFactuData?.facturation_email])

  const groupsForEditor = useMemo(() => payload?.facturationGroups || [], [payload])
  const rg2sForEditor = useMemo(
    () => [{ orgaId, rg2s: payload?.rg2s || [] }],
    [orgaId, payload]
  )
  const agenciesForEditor = useMemo(
    () =>
      softaccessFactuData
        ? [{ orga_id: orgaId, agencies: softaccessFactuData.allSoftAgencies || [] }]
        : [],
    [orgaId, softaccessFactuData]
  )

  const hasCgv = Boolean(softaccessFactuData?.cgvs)

  const updateSoftaccessInPayload = (patch: Partial<SoftaccessFactu>) => {
    if (!payload || !selectedSoftId) return
    setPayload({
      ...payload,
      softaccesses: payload.softaccesses.map(s =>
        s.softaccess_id === selectedSoftId ? { ...s, ...patch } : s
      ),
    })
  }

  const saveCgvData = async (dataJson: CgvDataJson) => {

    if (!softaccessFactuData) {
      message.error('Impossible de sauvegarder les CGV : aucun accès logiciel trouvé')
      return
    }
    setSaving(true)
    try {
      if (softaccessFactuData.cgvs) {
        await facturationCgvService.updateCgv(orgaId, {
          ...softaccessFactuData.cgvs,
          softaccess_type: softaccessFactuData.softaccess_type,
          softaccess_id: softaccessFactuData.softaccess_id,
          orga_id: orgaId,
          data_json: JSON.stringify(dataJson),
        })
        message.success('CGV mises à jour')
      } else {
        const newCgv = {
          cgv_id: v4(),
          orga_id: orgaId,
          softaccess_id: softaccessFactuData.softaccess_id,
          softaccess_type: softaccessFactuData.softaccess_type,
          softaccess_name: softaccessFactuData.softaccess_name,
          cgv_validated: false,
          cgv_version: 1,
          data_json: JSON.stringify(dataJson),
        }
        await facturationCgvService.saveCgv(orgaId, newCgv)
        message.success('CGV créées')
      }
      setCgvModalOpen(false)
      await load()
    } catch (e) {
      console.error(e)
      message.error('Erreur lors de la sauvegarde des CGV')
    } finally {
      setSaving(false)
    }
  }

  const onSendCgv = async () => {
    if (!softaccessFactuData?.cgvs) return
    const data = parseCgvDataJson(softaccessFactuData.cgvs.data_json)
    if (!data.email_signataire) {
      message.error("Renseignez l'email signataire dans les CGV avant l'envoi")
      return
    }
    setSending(true)
    try {
      const { data: res } = await facturationCgvService.sendCgv(
        orgaId,
        softaccessFactuData.cgvs.cgv_id
      )
      if (res.ok === false) {
        message.error(res.error || "Erreur lors de l'envoi")
        return
      }
      message.success('Email CGV envoyé (signataire + vous en copie)')
    } catch (e) {
      console.error(e)
      message.error("Erreur lors de l'envoi des CGV")
    } finally {
      setSending(false)
    }
  }

  const onUpdateGroupement = async (groupementId: string | undefined) => {
    if (!softaccessFactuData) return
    try {
      await facturationCgvService.updateGroupement(
        softaccessFactuData.softaccess_id,
        groupementId
      )
      message.success('Groupement mis à jour')
      await load()
    } catch (e) {
      console.error(e)
      message.error('Erreur lors de la mise à jour du groupement')
    }
  }

  const onUpdateTva = async (tvaEnable: boolean) => {
    if (!softaccessFactuData) return
    try {
      await facturationCgvService.updateTva(
        softaccessFactuData.facturation_id || softaccessFactuData.softaccess_id,
        tvaEnable
      )
      message.success('TVA mise à jour')
      updateSoftaccessInPayload({ tva_enable: tvaEnable })
    } catch (e) {
      console.error(e)
      message.error('Erreur lors de la mise à jour de la TVA')
    }
  }

  const addFacturation = async () => {
    if (!softaccessFactuData || !hasCgv) return
    const email = emailFacturation.trim()
    if (!EMAIL_FACTURATION_REGEX.test(email)) {
      message.error("L'adresse email n'est pas valide")
      return
    }
    setSaving(true)
    try {
      await facturationCgvService.saveFacturation(
        orgaId,
        softaccessFactuData.softaccess_type,
        softaccessFactuData.softaccess_id,
        email
      )
      message.success('Facturation créée')
      await load()
    } catch (e) {
      console.error(e)
      message.error("Erreur lors de l'ajout de la facturation")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <Spin />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Chargement des données de CGV / Facturation...
        </Typography.Text>
      </div>
    )
  }

  const cgvValidated = Boolean(softaccessFactuData?.cgvs?.cgv_validated)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      <Card
        size="small"
        title={
          <Space>
            <span>CGV</span>
            {!hasCgv && <Tag>Absentes</Tag>}
            {hasCgv && cgvValidated && (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Validées
              </Tag>
            )}
            {hasCgv && !cgvValidated && (
              <Tag icon={<CloseCircleOutlined />} color="warning">
                Non validées
              </Tag>
            )}
            {softaccessFactuData?.softaccess_type && <Tag color="blue" icon={<AppstoreFilled />}>{softLabel(softaccessFactuData.softaccess_type)}</Tag>}
          </Space>
        }
      >
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={8}>
            <Button
              icon={hasCgv ? <EditOutlined /> : <PlusOutlined />}
              type="primary"
              block
              onClick={() => setCgvModalOpen(true)}
            >
              {hasCgv ? 'Modifier les infos CGV' : 'Ajouter les CGV'}
            </Button>
          </Col>
          <Col xs={24} sm={8}>
            <Button
              icon={<SendOutlined />}
              type="primary"
              ghost
              block
              disabled={!hasCgv}
              loading={sending}
              onClick={onSendCgv}
            >
              Envoyer les CGV
            </Button>
          </Col>
          <Col xs={24} sm={8}>
            <Button
              icon={<LinkOutlined />}
              block
              disabled={!hasCgv || !softaccessFactuData?.cgvs?.cgv_id}
              onClick={() =>
                softaccessFactuData?.cgvs?.cgv_id &&
                copyCgvSignatureLink(softaccessFactuData.cgvs.cgv_id)
              }
            >
              Copier le lien de signature
            </Button>
          </Col>
        </Row>
      </Card>

      <CgvEditModal
        open={cgvModalOpen}
        onClose={() => setCgvModalOpen(false)}
        onSave={saveCgvData}
        dataJson={softaccessFactuData?.cgvs?.data_json}
        cgvId={softaccessFactuData?.cgvs?.cgv_id}
        initialSiret={orgaSiret || payload?.orga_siret}
        saving={saving}
        isEdit={hasCgv}
      />

      {softaccessFactuData && (
        <Card
          size="small"
          title={
            <Space>
              <span>FACTURATION</span>
              <Tag icon={<CreditCardFilled />} color="gold">
                {softaccessFactuData.nb_facturation} utilisateur{softaccessFactuData.nb_facturation > 1 ? 's' : ''} facturé{softaccessFactuData.nb_facturation > 1 ? 's' : ''}
              </Tag>
            </Space>
          }
        >
          <Row gutter={[10, 10]}>
            <Col xs={24} sm={16}>
              <div style={chipStyle}>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                  Groupement
                </Typography.Text>
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="Aucun"
                  value={softaccessFactuData.groupement || undefined}
                  onChange={v => onUpdateGroupement(v)}
                  options={groupements.map(g => ({ value: g.id, label: g.name }))}
                />
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div
                style={{
                  ...chipStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  TVA activée ?
                </Typography.Text>
                <Space size={8}>
                  <Switch
                    size="small"
                    checked={softaccessFactuData.tva_enable}
                    onChange={onUpdateTva}
                    disabled={softaccessFactuData.facturation_id ? false : true}
                  />
                </Space>
              </div>
            </Col>
            <Col span={24}>
              <div style={chipStyle}>
                {softaccessFactuData.facturation_id ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Space>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Contact
                      </Typography.Text>
                      <MailOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                      <Typography.Text copyable={!!softaccessFactuData.facturation_email}>
                        {softaccessFactuData.facturation_email || '-'}
                      </Typography.Text>
                    </Space>
                    <Tag
                      color={softaccessFactuData.facturation_email ? 'success' : 'default'}
                      style={{ margin: 0 }}
                    >
                      {softaccessFactuData.facturation_email ? 'Active' : 'Aucun'}
                    </Tag>
                  </div>
                ) : (
                  <>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12, display: 'block', marginBottom: 8 }}
                    >
                      Contact
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      Pour renseigner l'email de facturation, veuillez d'abord renseigner les CGV.
                    </Typography.Text>
                    <Space wrap>
                      <Input
                        style={{ width: 260 }}
                        placeholder="Email de facturation"
                        disabled={!hasCgv}
                        value={emailFacturation}
                        onChange={e => setEmailFacturation(e.target.value)}
                        prefix={<MailOutlined />}
                      />
                      <Button
                        type="primary"
                        disabled={!hasCgv}
                        loading={saving}
                        onClick={addFacturation}
                      >
                        Valider
                      </Button>
                    </Space>
                  </>
                )}
              </div>
            </Col>
          </Row>

          {softaccessFactuData.facturation_id && softaccessFactuData.softaccess_type === 'cnegoce' && (
            <div style={{ marginTop: 12 }}>
              {groupsForEditor.length === 0 && (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  Aucun groupe multi-agences pour l&apos;instant. Vous pouvez en ajouter ci-dessous
                  (même flux que l&apos;onglet Facturation).
                </Typography.Paragraph>
              )}
              <FacturationGroups
                groups={groupsForEditor}
                rg2s={rg2sForEditor}
                agencies={agenciesForEditor}
                lockedOrganisation={{
                  orga_id: orgaId,
                  softaccess_id: softaccessFactuData.softaccess_id,
                  name: payload?.orga_name || orgaId,
                  defaultGroupId: softaccessFactuData.facturation_id,
                }}
                saveFacturationGroupsCallback={() => {
                  load()
                }}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
